import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import ApiService from '../services/apiService';
import SearchNavigation from '../components/SearchNavigation';

const News = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [blogPosts, setBlogPosts] = useState([]);
    const [searchText, setSearchText] = useState("");

    useEffect(() => {
        document.body.style.paddingTop = '0';
        return () => {
            document.body.style.paddingTop = '';
        };
    }, []);

    useEffect(() => {
        async function fetchData() {
            const apiResponse = await ApiService.getNews(4000000);
            setBlogPosts(apiResponse || []);
            setIsLoading(false);
        }
        fetchData();
    }, []);

    const formatDate = (dateStr) => {
        if (dateStr) {
            try {
                if (dateStr.length === 8) {
                    const year = dateStr.substring(0, 4);
                    const month = dateStr.substring(4, 6);
                    const day = dateStr.substring(6, 8);
                    return `${day}/${month}/${year}`;
                }
                return dateStr;
            } catch {
                return dateStr;
            }
        }
        return 'Date not available';
    };

    const filteredItems = blogPosts.filter(item =>
        item.title.toLowerCase().includes(searchText.toLowerCase())
    );

    if (isLoading) {
        return (
            <div className="news-layout">
                <Navbar />
                <SearchNavigation />
                <div className="news-main-content">
                    <div className="loading-state">
                        <i className="bx bx-loader-alt bx-spin"></i>
                        <p>Loading news...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="news-layout">
            <Navbar />
            <SearchNavigation />

            <div className="news-main-content">
                {/* Hero Section */}
                <div className="news-hero">
                    <div className="hero-badge">
                        <i className="bx bx-news"></i>
                        <span>Legal News</span>
                    </div>
                    <h1 className="hero-title">Latest Legal News</h1>
                    <p className="hero-subtitle">
                        Stay informed with the latest legal developments and court updates
                    </p>
                </div>

                {/* Container */}
                <div className="news-container">
                    {/* Search Header */}
                    <div className="search-header">
                        <div className="search-box">
                            <i className="bx bx-search search-icon"></i>
                            <input
                                type="text"
                                className="search-input"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                placeholder="Search news articles..."
                                disabled={isLoading}
                            />
                            {searchText && (
                                <button
                                    className="clear-search"
                                    onClick={() => setSearchText('')}
                                >
                                    <i className="bx bx-x"></i>
                                </button>
                            )}
                        </div>
                        <div className="results-count">
                            Showing <strong>{filteredItems.length}</strong> of <strong>{blogPosts.length}</strong> articles
                        </div>
                    </div>

                    {/* News Grid */}
                    <div className="news-grid">
                        {filteredItems.length === 0 ? (
                            <div className="empty-state">
                                <i className="bx bx-search-alt"></i>
                                <p>No news articles found matching "{searchText}"</p>
                            </div>
                        ) : (
                            filteredItems.map((item, index) => (
                                <article key={item.id} className="news-card">
                                    <a href={`/NewsDetail/${item.id}`} className="news-link">
                                        <div className="news-image-wrapper">
                                            <img 
                                                src={`https://legaleagleweb.com/newsimage/${item.image}`} 
                                                alt={item.title} 
                                                className="news-image" 
                                                loading="lazy"
                                                onError={(e) => {
                                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect width="400" height="300" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="18" fill="%239ca3af"%3ENo Image%3C/text%3E%3C/svg%3E';
                                                }}
                                            />
                                            <div className="news-overlay">
                                                <i className="bx bx-right-arrow-alt"></i>
                                            </div>
                                        </div>
                                        <div className="news-content">
                                            <h3 className="news-title">{item.title}</h3>
                                            <div className="news-meta">
                                                <div className="meta-item">
                                                    <i className="bx bx-calendar"></i>
                                                    <span>{formatDate(item.date)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </a>
                                </article>
                            ))
                        )}
                    </div>
                </div>
            </div>

            <style>{`
                /* ============================================
                   BASE LAYOUT
                ============================================ */
                .news-layout {
                    min-height: 100vh;
                    background: #fafafa;
                }

                .news-main-content {
                    padding-top: 140px;
                    width: 100%;
                }

                /* ============================================
                   HERO SECTION
                ============================================ */
                .news-hero {
                    background: linear-gradient(135deg, #E91E63 0%, #E91E64 25%, #c41849 75%, #D81B60 100%);
                    padding: 50px 20px 40px;
                    text-align: center;
                    color: white;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
                }

                .hero-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 20px;
                    background: rgba(255, 255, 255, 0.2);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.3);
                    border-radius: 50px;
                    font-size: 13px;
                    font-weight: 600;
                    margin-bottom: 16px;
                }

                .hero-badge i {
                    font-size: 16px;
                }

                .hero-title {
                    font-size: 2rem;
                    font-weight: 800;
                    margin: 0 0 10px 0;
                    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
                    letter-spacing: -0.5px;
                    line-height: 1.2;
                }

                .hero-subtitle {
                    font-size: 1rem;
                    opacity: 0.95;
                    margin: 0;
                    font-weight: 400;
                }

                /* ============================================
                   CONTAINER
                ============================================ */
                .news-container {
                    max-width: 1400px;
                    margin: -30px auto 0;
                    padding: 0 20px 40px;
                    position: relative;
                    z-index: 10;
                }

                /* ============================================
                   SEARCH HEADER
                ============================================ */
                .search-header {
                    background: white;
                    border-radius: 16px;
                    padding: 20px 24px;
                    margin-bottom: 24px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    flex-wrap: wrap;
                }

                .search-box {
                    position: relative;
                    flex: 1;
                    min-width: 280px;
                }

                .search-icon {
                    position: absolute;
                    left: 16px;
                    top: 50%;
                    transform: translateY(-50%);
                    font-size: 1.25rem;
                    color: #9ca3af;
                    pointer-events: none;
                }

                .search-input {
                    width: 100%;
                    padding: 12px 45px 12px 48px;
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    font-size: 0.95rem;
                    transition: all 0.3s ease;
                    background: #fafafa;
                }

                .search-input:focus {
                    outline: none;
                    border-color: #E91E63;
                    background: white;
                    box-shadow: 0 0 0 4px rgba(245, 166, 35, 0.1);
                }

                .search-input:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .clear-search {
                    position: absolute;
                    right: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 24px;
                    height: 24px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #e5e7eb;
                    border: none;
                    border-radius: 50%;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .clear-search:hover {
                    background: #d1d5db;
                }

                .clear-search i {
                    font-size: 1rem;
                    color: #6b7280;
                }

                .results-count {
                    font-size: 0.95rem;
                    color: #6b7280;
                    white-space: nowrap;
                }

                .results-count strong {
                    color: #E91E63;
                    font-weight: 700;
                }

                /* ============================================
                   NEWS GRID
                ============================================ */
                .news-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
                    gap: 24px;
                }

                .news-card {
                    background: white;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
                    transition: all 0.3s ease;
                    border: 2px solid transparent;
                }

                .news-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 30px rgba(245, 166, 35, 0.15);
                    border-color: #E91E63;
                }

                .news-link {
                    text-decoration: none;
                    color: inherit;
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                }

                .news-image-wrapper {
                    position: relative;
                    width: 100%;
                    height: 220px;
                    overflow: hidden;
                    background: #f3f4f6;
                }

                .news-image {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transition: transform 0.3s ease;
                }

                .news-card:hover .news-image {
                    transform: scale(1.05);
                }

                .news-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: linear-gradient(135deg, rgba(245, 166, 35, 0.9), rgba(233, 30, 99, 0.9));
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    opacity: 0;
                    transition: opacity 0.3s ease;
                }

                .news-card:hover .news-overlay {
                    opacity: 1;
                }

                .news-overlay i {
                    font-size: 3rem;
                    color: white;
                    transform: translateX(-10px);
                    transition: transform 0.3s ease;
                }

                .news-card:hover .news-overlay i {
                    transform: translateX(0);
                }

                .news-content {
                    padding: 20px;
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                    flex: 1;
                }

                .news-title {
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: #1a1a1a;
                    margin: 0;
                    line-height: 1.4;
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }

                .news-meta {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    flex-wrap: wrap;
                    margin-top: auto;
                    padding-top: 12px;
                    border-top: 1px solid #f0f0f0;
                }

                .meta-item {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 0.85rem;
                    color: #6b7280;
                }

                .meta-item i {
                    font-size: 1rem;
                    color: #E91E63;
                }

                /* ============================================
                   LOADING STATE
                ============================================ */
                .loading-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    min-height: 60vh;
                    gap: 16px;
                }

                .loading-state i {
                    font-size: 3rem;
                    color: #E91E63;
                }

                .loading-state p {
                    font-size: 1.1rem;
                    color: #6b7280;
                    margin: 0;
                }

                /* ============================================
                   EMPTY STATE
                ============================================ */
                .empty-state {
                    grid-column: 1 / -1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 80px 20px;
                    text-align: center;
                }

                .empty-state i {
                    font-size: 4rem;
                    color: #d1d5db;
                    margin-bottom: 16px;
                }

                .empty-state p {
                    font-size: 1.1rem;
                    color: #6b7280;
                    margin: 0;
                }

                /* ============================================
                   RESPONSIVE DESIGN
                ============================================ */
                @media (max-width: 768px) {
                    .news-main-content {
                        padding-top: 130px;
                    }

                    .news-hero {
                        padding: 35px 15px 30px;
                    }

                    .hero-title {
                        font-size: 1.6rem;
                    }

                    .hero-subtitle {
                        font-size: 0.95rem;
                    }

                    .news-container {
                        margin-top: -20px;
                        padding: 0 15px 30px;
                    }

                    .search-header {
                        flex-direction: column;
                        align-items: stretch;
                        padding: 16px;
                    }

                    .search-box {
                        min-width: 100%;
                    }

                    .results-count {
                        text-align: center;
                    }

                    .news-grid {
                        grid-template-columns: 1fr;
                        gap: 16px;
                    }

                    .news-image-wrapper {
                        height: 200px;
                    }

                    .news-content {
                        padding: 16px;
                    }

                    .news-title {
                        font-size: 1rem;
                    }
                }

                @media (max-width: 480px) {
                    .hero-title {
                        font-size: 1.4rem;
                    }

                    .hero-badge {
                        padding: 6px 14px;
                        font-size: 12px;
                    }

                    .news-image-wrapper {
                        height: 180px;
                    }
                }

                /* ============================================
                   REMOVE TOP WHITE SPACE
                ============================================ */
                body {
                    margin: 0 !important;
                    padding: 0 !important;
                }
            `}</style>
        </div>
    );
}

export default News;